import { createFileRoute, Link } from "@tanstack/react-router";
import GameDayPage, { buildGameDaySchemas, Section, SITE, type Faq } from "@/components/GameDayPage";
import heroImage from "@/assets/mercedes-benz-stadium-sprinter-limo-party-bus.jpg?format=webp&quality=72&w=1400&imagetools";
import limoImage from "@/assets/limo-style-sprinter-party-bus-lounge-bar.jpg?format=webp&quality=72&w=1000&imagetools";
import execImage from "@/assets/sprinter-exec-green.jpg?format=webp&quality=72&w=1000&imagetools";
import jetImage from "@/assets/jet-style-sprinter-white-interior-captain-seats.jpg?format=webp&quality=72&w=1000&imagetools";
import suvImage from "@/assets/suburban-suv-game-day-atlanta.webp?format=webp&quality=72&w=1000&imagetools";

import suvCaptainImage from "@/assets/luxury-suburban-suv-captain-seats-atlanta.jpg?format=webp&quality=72&w=1000&imagetools";

const PAGE_URL = `${SITE}/mercedes-benz-stadium-transportation`;
const TITLE = "Falcons Game Limo Service | Mercedes-Benz Stadium Sprinter, Atlanta";
const DESC =
  "Arrive at Mercedes-Benz Stadium for the Falcons, Atlanta United, the SEC Championship and concerts in a custom Sprinter limo. Drop-off near the gates, chauffeur waiting after.";

const faqs: Faq[] = [
  { q: "Where do limos drop off at Mercedes-Benz Stadium?", a: "As close to your gate as event road closures allow, and your chauffeur texts the pickup spot before the event ends." },
  { q: "How early should we leave Buckhead or Midtown?", a: "Plan to leave about 90 minutes before kickoff or showtime. Big events close streets early, and security lines run long." },
  { q: "Can you pick us up right after the game or concert?", a: "Yes. Your chauffeur waits nearby during the event and is ready as soon as you walk out." },
  { q: "Do you also do State Farm Arena and Truist Park?", a: "Yes. We run Sprinter limos to State Farm Arena, Truist Park and every major Atlanta venue." },
  { q: "How far ahead should we book SEC Championship weekend?", a: "As early as possible. SEC Championship weekend is one of the busiest in Atlanta, and our custom fleet is small." },
  { q: "Can we bring alcohol?", a: "Yes, it is BYOB. Guests 21+ may bring their own drinks, and the bar is set with glassware and ice. We do not provide or sell alcohol." },
];

export const Route = createFileRoute("/mercedes-benz-stadium-transportation")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: buildGameDaySchemas({
      url: PAGE_URL,
      name: "Mercedes-Benz Stadium Sprinter Limo Service",
      description: DESC,
      serviceType: "Stadium and event transportation",
      areas: ["Atlanta"],
      faqs,
      crumb: "Mercedes-Benz Stadium Transportation",
    }),
  }),
  component: Page,
});

function Page() {
  return (
    <GameDayPage
      eyebrow="Mercedes-Benz Stadium · Atlanta"
      h1="Mercedes-Benz Stadium Transportation: Falcons, SEC Championship, Concerts & More"
      intro="You've got the tickets. I'll plan your Mercedes-Benz Stadium arrival and pickup, with a custom Sprinter or private Suburban and a chauffeur who stays nearby."
      heroImg={heroImage}
      heroAlt="Sprinter limo drop-off at Mercedes-Benz Stadium, Atlanta"
      champagneText="BYOB: guests 21+ may bring their own drinks. The bar is set with glassware and ice. We do not provide or sell alcohol."
      smallPrint="BYOB for guests 21+. We do not provide or sell alcohol."
      differentiation={{
        title: "Not a party bus. One of Atlanta's newest custom-built Sprinter fleets.",
        customBuiltText: "No tired buses, no matching fleet. Every Sprinter is one of one.",
      }}
      bigDates={{
        heading: "Big Games and Events We're Booking",
        events: ["Georgia vs. Florida", "SEC Championship"],
        note: "Also booking Falcons and Atlanta United home games, the Peach Bowl and major concerts.",
      }}
      steps={{
        title: "Skip the Parking and Traffic",
        items: [
          { t: "Drop-off near the stadium", d: "We get you as close to your gate as closures allow." },
          { t: "No parking lots", d: "No circling downtown, no garage fees, no long walk from a remote lot." },
          { t: "Chauffeur waiting", d: "Your chauffeur stays staged nearby for the whole game or concert." },
          { t: "A clear pickup plan", d: "Your chauffeur texts the meeting point before the event ends." },
        ],
      }}
      middle={
        <Section
          title="Pre-Game and After-Party"
          intro={
            <>
              <p>Make it a whole night. We pick up at Buckhead hotels like the St. Regis, Four Seasons and Waldorf Astoria, take you to dinner at Hal's, Nobu, STK or Chops Lobster Bar, then on to the stadium. After the final whistle or the encore, the after-party is one short ride away.</p>
              <p>Whether it's a Falcons game limo, SEC Championship transportation for your group or a concert night with your friends, you get one chauffeur and one Sprinter for the whole evening. Heading to Georgia vs. Florida on Oct 31? See our <Link to="/georgia-vs-florida-game-transportation" className="text-primary underline">Georgia vs. Florida transportation</Link> page.</p>
            </>
          }
        />
      }
      vehicles={{
        title: "Pick Your Ride",
        intro: "Every vehicle is chauffeured, spotless and custom-built. Choose the one that fits your crew.",
        list: [
          { name: "Limo Style Sprinter", cap: "Up to 14 guests", detail: "Lounge seating, a built-in bar, 2 TVs, LED ceiling and premium sound.", img: limoImage, alt: "Limo style Sprinter lounge interior with built-in bar at Mercedes-Benz Stadium" },
          { name: "Executive Sprinter", cap: "Up to 13 guests", detail: "Premium leather and a more relaxed ride downtown.", img: execImage, alt: "Executive Sprinter limo interior for a Mercedes-Benz Stadium group" },
          { name: "Jet Style Sprinter", cap: "Up to 7 guests", detail: "White leather captain seats and first-class comfort for smaller groups.", img: jetImage, alt: "Jet style Sprinter with white leather captain seats for a Mercedes-Benz Stadium event" },
          { name: "Luxury Suburban SUV, Captain Seats", cap: "Up to 6 guests", detail: "Leather captain's chairs for a smaller, private ride.", img: suvCaptainImage, alt: "Luxury Suburban SUV with leather captain seats for game day in Atlanta" },
          { name: "Suburban SUV, Bench Seats", cap: "Up to 7 guests", detail: "Bench seating for a group of up to 7.", img: suvImage, alt: "Black Suburban SUV with bench seating for game day in Atlanta" },
        ],
        note: "The bar, TVs, sound system, LED lighting, stand-up headroom and CarPlay are on every Sprinter. Our Suburbans are a quieter, private ride for smaller groups.",
      }}
      faqTitle="Mercedes-Benz Stadium Transportation FAQ"
      faqs={faqs}
      occasion="Concert/Event"
      bookTitle="Text Me Your Event Date"
    />
  );
}
